import type { MaybeRefOrGetter } from 'vue';
import type { ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { computed, toValue } from 'vue';
import { callOrReturn } from '#layers/base';
import { getBlockConfig, isBlockDefinition, useBlockEditorLibrary } from '#layers/block-engine';
import { getFieldDisplayBlockOptions } from '#layers/data-model/utils/fields/field-display';

export function usePrimitiveFieldDisplay(field: MaybeRefOrGetter<ResolvedMonospacePrimitiveField>) {
	const computedField = computed(() => toValue(field));
	const { getBlock, isReady } = useBlockEditorLibrary();

	const base = computed(() => getFieldDisplayBlockOptions(computedField.value));

	const blockDef = computed(() => {
		const block = getBlock(base.value.block);
		if (!isBlockDefinition(block)) return;
		return block;
	});

	// True until the shared block library has finished its first fetch. Prevents
	// callers from briefly rendering the empty-state fallback while the lookup
	// is still pending (e.g. first navigation to a layout before cache warm-up).
	const isResolving = computed(() => !isReady.value && !blockDef.value);

	const defaultProps = computed(() => {
		if (!blockDef.value) return null;

		const blockConfig = getBlockConfig(blockDef.value);

		return callOrReturn(blockConfig?.fieldBinding?.defaultProps, {
			primitiveType: computedField.value.type,
			fieldTypeId: computedField.value.meta?.typeTemplateId ?? '',
			typeOptions: computedField.value.meta?.typeOptions ?? {},
		});
	});

	return {
		block: computed(() => blockDef.value?.id),
		props: computed(() => ({ ...defaultProps.value, ...computedField.value.meta?.typeOptions, ...base.value.props })),
		isResolving,
	};
}

export function useRelationFieldDisplay(field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField>) {
	const computedField = computed(() => toValue(field));
	const base = computed(() => getFieldDisplayBlockOptions(computedField.value));

	return {
		block: computed(() => base.value.block),
		props: computed(() => base.value.props),
	};
}
